import React from "react";
import { Helmet } from "react-helmet-async";

// Interim OG image (Spain street from the hero). Final branded OG asset pending.
const DEFAULT_OG = "/img/home-hero.jpg";

// Canonical host (apex, no www). Single source for canonical/og:url/og:image/JSON-LD URLs.
export const SITE_URL = (process.env.REACT_APP_SITE_URL || "").replace(/\/$/, "");

const SITE_NAME = "Destino Europe";
const SITE_DESCRIPTION =
  "Destino Europe, by Espíritu Travel, helps Americans plan and manage their move to Spain with knowledgeable support on the ground. Your new life, together.";
const DEFAULT_TITLE = `${SITE_NAME} — Your new life, together.`;

// Wrap any page to set proper title/description/OG/canonical + optional JSON-LD.
// Props:
//  - title, description, image (optional)
//  - path (current path; canonical built from it)
//  - jsonLd: optional object or array of objects to inline as <script type="application/ld+json">
export default function Seo({
  title,
  description = SITE_DESCRIPTION,
  image,
  imageAlt,
  path,
  jsonLd,
  type = "website",
  noindex = false,
}) {
  const fullTitle = title ? `${title}` : DEFAULT_TITLE;
  const origin = SITE_URL;
  const rawImage = image || DEFAULT_OG;
  const ogImage = rawImage.startsWith("http") ? rawImage : `${origin}${rawImage}`;
  const url = `${origin}${path || (typeof window !== "undefined" ? window.location.pathname : "/")}`;

  const ldArr = !jsonLd ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      {ldArr.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

// Reusable schemas
export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Destino Europe",
  url: "https://destinoeurope.com",
  inLanguage: "en-US",
};

export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Destino Europe",
  alternateName: "Destino Europe by Espíritu Travel",
  slogan: "Your new life, together.",
  url: SITE_URL,
  logo: `${SITE_URL}/icon-512.png`,
  description: SITE_DESCRIPTION,
  parentOrganization: { "@type": "Organization", name: "Espíritu Travel" },
  sameAs: [],
};

export function buildArticleSchema({
  title,
  description,
  image,
  author = "Destino Europe",
  datePublished,
  dateModified,
  url,
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    inLanguage: "en-US",
    headline: title,
    description,
    image: image ? [image] : undefined,
    author: { "@type": "Organization", name: author },
    publisher: ORGANIZATION_SCHEMA,
    datePublished,
    dateModified: dateModified || datePublished,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };
}

export function buildFaqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}
